import { Inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { of } from "rxjs";
import { catchError } from "rxjs/operators";
import { API_BASE_URL } from "src/swagger/api.client.generated";
import { FEATURE_FLAG_FALLBACK_DEFAULTS, FeatureFlagKey } from "./feature-flag.models";

interface ResolvedFlagsResponse {
  resultStatus?: { statusCode?: number; errorText?: string };
  responseData?: Record<string, boolean> | null;
}

/**
 * FLIP-304. Uses the direct-HttpClient + API_BASE_URL pattern rather than the NSwag-generated
 * client, matching NovaInsightService/CommunityWarmupService - the generated client is built
 * from a live API instance (`npm run nswag`), which isn't practical to regenerate here.
 *
 * `init()` is called once per authenticated session from app.component's
 * maybeLoadAuthenticatedData(), alongside the app's other one-shot boot-time service
 * initialisers (CardService, HomeService, ...). `isEnabled()` is synchronous and safe to call
 * before `init()` resolves (or if it never does) - it just falls back to
 * FEATURE_FLAG_FALLBACK_DEFAULTS, so a flag outage degrades to a known-safe state rather than
 * throwing or leaving a caller to handle a Promise.
 */
@Injectable({
  providedIn: "root",
})
export class FeatureFlagService {
  private resolvedFlags: Record<string, boolean> | null = null;
  private initPromise: Promise<void> | null = null;

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {}

  /** Fetches the resolved flag set for the current user. Safe to call more than once - later calls return the same in-flight/completed promise rather than re-fetching. */
  init(): Promise<void> {
    if (!this.initPromise) {
      // .toPromise() rather than firstValueFrom, which doesn't exist in RxJS 6 (this app pins
      // ~6.6.7) - it's deprecated in RxJS 7+ but is the correct choice on this version.
      this.initPromise = this.http
        .get<ResolvedFlagsResponse>(`${this.apiBaseUrl}/api/FeatureFlags/GetResolvedFlags`)
        .pipe(catchError(() => of(null as ResolvedFlagsResponse | null)))
        .toPromise()
        .then((response) => {
          this.resolvedFlags = response?.responseData ?? null;
        });
    }
    return this.initPromise;
  }

  /**
   * Synchronous - reads whatever `init()` has resolved so far. Resolves to
   * FEATURE_FLAG_FALLBACK_DEFAULTS[key] (or `false` for an unknown key) when `init()` hasn't
   * completed yet, failed, or the key wasn't in the response - a flag outage degrades to a known
   * default rather than blanking the feature or throwing.
   */
  isEnabled(key: FeatureFlagKey): boolean {
    if (this.resolvedFlags && key in this.resolvedFlags) {
      return this.resolvedFlags[key];
    }
    return FEATURE_FLAG_FALLBACK_DEFAULTS[key] ?? false;
  }

  /** Clears the cached resolution so the next `init()` call re-fetches - call on logout so a different account logging into the same tab doesn't briefly see the previous account's flags. */
  reset(): void {
    this.resolvedFlags = null;
    this.initPromise = null;
  }
}
